<template>
    <q-dialog
        v-model="show"
        transition-show="flip-down"
        transition-hide="flip-up"
        persistent
    >
        <q-card class="notification">
            <q-card-section class="bg-white text-black q-pa-none">
                <q-bar
                    :class="
                        !inEventAlertNotificationMode
                            ? 'justification-notification-header'
                            : 'event-alert-notification-header'
                    "
                >
                    <div
                        class="row items-center justify-between full-height full-width text-medium-grey"
                        :class="
                            inEventAlertNotificationMode
                                ? 'event-alert-text-color'
                                : 'justification-text-color'
                        "
                    >
                        <div class="vertical-align modal-title font-improve">
                            <q-icon
                                v-if="!inEventAlertNotificationMode"
                                name="fa-regular fa-circle-exclamation"
                                class="q-ma-sm q-mr-md"
                                size="sm"
                            />
                            <span class="vertical-middle">
                                {{
                                    !inEventAlertNotificationMode
                                        ? $t('Justification Requested')
                                        : $t('Notifications')
                                }}
                            </span>
                            <q-badge
                                color="grey-4"
                                class="q-ml-sm text-bold"
                                rounded
                                style="font-size: 14px"
                                :class="
                                    inEventAlertNotificationMode
                                        ? 'event-alert-text-color'
                                        : 'justification-text-color'
                                "
                                >{{ totalNotificationsCount }}</q-badge
                            >
                        </div>

                        <!-- Close button "x"-->
                        <div class="q-mr-sm">
                            <q-btn
                                :disable="!allowClose"
                                size="md"
                                flat
                                round
                                dense
                                icon="close"
                                @click="close"
                            >
                                <q-tooltip>
                                    {{
                                        $t(
                                            'To close this popup, you must resolve all new notifications.'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </div>
                </q-bar>
            </q-card-section>
            <q-card-section
                v-if="!inEventAlertNotificationMode"
                class="q-pb-none notification-body"
            >
                <div class="q-mb-md">
                    {{
                        'Here are the unjustified events in the current shift that require your attention'
                    }}
                </div>

                <downtime-notification-card
                    v-for="(notification, index) in newDowntimeNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="index"
                    class="q-mb-md list-complete-item"
                />
                <quality-notification-card
                    v-for="(notification, index) in newQualityNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="newDowntimeNotifications.length + index"
                    class="q-mb-md list-complete-item"
                />
                <downtime-notification-card
                    v-for="(notification, index) in oldDowntimeNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="index"
                    class="q-mb-md list-complete-item"
                />
                <quality-notification-card
                    v-for="(notification, index) in oldQualityNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="oldDowntimeNotifications.length + index"
                    class="q-mb-md list-complete-item"
                />
            </q-card-section>
            <q-card-section
                v-if="inEventAlertNotificationMode"
                class="q-pb-none notification-body"
            >
                <event-alert-notification-card
                    v-for="(notification, index) in newEventAlertNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="index"
                    class="q-mb-md list-complete-item"
                />
                <event-alert-notification-card
                    v-for="(notification, index) in oldEventAlertNotifications"
                    :key="notification.eventUUID"
                    :notification="notification"
                    :index="oldEventAlertNotifications.length + index"
                    class="q-mb-md list-complete-item"
                />
            </q-card-section>
            <q-separator />
            <q-card-actions align="right" class="q-pa-md">
                <q-btn
                    v-if="allowDismiss"
                    outline
                    color="secondary"
                    text-color="secondary"
                    @click="dismissAll"
                    >{{ $t('Dismiss all') }}</q-btn
                >
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import DowntimeNotificationCard from '@components/DowntimeNotificationCard.vue';
import QualityNotificationCard from '@components/QualityNotificationCard.vue';
import EventAlertNotificationCard from '@components/EventAlertNotificationCard.vue';
import { useNotificationsStore } from '@stores/notifications.ts';
import { mapActions } from 'pinia';

export default {
    name: 'NotificationHub',
    components: {
        DowntimeNotificationCard,
        QualityNotificationCard,
        EventAlertNotificationCard
    },
    props: {
        notifications: {
            validator(value) {
                return Array.isArray(value) || value === null;
            },
            required: false,
            default: []
        },
        qualityNotifications: {
            validator(value) {
                return Array.isArray(value) || value === null;
            },
            required: false,
            default: []
        },
        eventAlertNotifications: {
            validator(value) {
                return Array.isArray(value) || value === null;
            },
            required: false,
            default: []
        }
    },
    watch: {
        totalNotificationsCount: {
            handler() {
                // if there are no notifications, close the dialog
                if (this.totalNotificationsCount === 0) {
                    this.close();
                }
            },
            immediate: true
        },
        newDowntimeNotifications: {
            handler() {
                this.sortNotificationsByReceivedAt(
                    this.newDowntimeNotifications
                );
                this.sortNotificationsByReceivedAt(
                    this.oldDowntimeNotifications
                );
            },
            immediate: true
        },
        newQualityNotifications: {
            handler() {
                this.sortNotificationsByReceivedAt(
                    this.newQualityNotifications
                );
                this.sortNotificationsByReceivedAt(
                    this.oldQualityNotifications
                );
            },
            immediate: true
        },
        newEventAlertNotifications: {
            handler() {
                this.sortNotificationsByReceivedAt(
                    this.newEventAlertNotifications
                );
                this.sortNotificationsByReceivedAt(
                    this.oldEventAlertNotifications
                );
            }
        }
    },
    data() {
        return {
            show: true
        };
    },
    computed: {
        /**
         * Allow dismiss notification popup
         * @returns {boolean}
         */
        allowDismiss() {
            // check if the user is always allowed to dismiss
            if (
                !this.inEventAlertNotificationMode &&
                this.$userCan('always_dismiss', 'downtime_notifications') &&
                this.$userCan('always_dismiss', 'quality_notifications')
            ) {
                return true;
            } else if (
                this.inEventAlertNotificationMode &&
                this.$userCan('always_dismiss', 'quality_notifications')
            ) {
                return true;
            }
            return false;
        },
        /**
         * Allow close notification popup
         * @returns {boolean}
         */
        allowClose() {
            // if the user cannot always dismiss, then they can close the dialog if there are no new notifications

            return this.allowDismiss || !this.newNotificationsCount;
        },
        /**
         * Total notifications count
         * @returns {boolean}
         */
        totalNotificationsCount() {
            if (!this.inEventAlertNotificationMode) {
                return (
                    this.notifications.length + this.qualityNotifications.length
                );
            }

            return this.eventAlertNotifications.length;
        },
        /**
         * New notifications
         * @returns {number}
         */
        newNotificationsCount() {
            return (
                this.newDowntimeNotifications.length +
                this.newQualityNotifications.length +
                this.newEventAlertNotifications.length
            );
        },
        /**
         * New downtime notifications
         * @returns {Array}
         */
        newDowntimeNotifications() {
            return this.notifications.filter(
                notification => notification.isNew
            );
        },
        /**
         * Old downtime notifications
         * @returns {Array}
         */
        oldDowntimeNotifications() {
            return this.notifications.filter(
                notification => !notification.isNew
            );
        },
        /**
         * New quality notifications
         * @returns {Array}
         */
        newQualityNotifications() {
            return this.qualityNotifications.filter(
                notification => notification.isNew
            );
        },
        /**
         * Old quality notifications
         * @returns {Array}
         */
        oldQualityNotifications() {
            return this.qualityNotifications.filter(
                notification => !notification.isNew
            );
        } /**
         * New quality notifications
         * @returns {Array}
         */,
        newEventAlertNotifications() {
            return this.eventAlertNotifications.filter(
                notification => notification.isNew
            );
        },
        /**
         * Old quality notifications
         * @returns {Array}
         */
        oldEventAlertNotifications() {
            return this.eventAlertNotifications.filter(
                notification => !notification.isNew
            );
        },
        /**
         * Check if this is in event alert notification mode or not
         */
        inEventAlertNotificationMode() {
            return (
                this.notifications.length === 0 &&
                this.qualityNotifications.length === 0 &&
                this.eventAlertNotifications.length > 0
            );
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [
            'dismissNotificationAction',
            'dismissQualityNotificationAction',
            'dismissEventAlertNotificationAction',
            'showNotificationPopupAction',
            'markAllNotificationsAsOldAction'
        ]),
        /**
         * Dismiss all notifications.
         */
        async dismissAll() {
            // check if the user is always allowed to dismiss
            if (!this.allowDismiss) {
                this.$notifyError(
                    this.$t('You do not have permission to dismiss events.')
                );
                return;
            }

            try {
                for (const notification of this.notifications) {
                    // dismiss notifications
                    this.dismissNotificationAction(notification['eventUUID']);
                }

                for (const notification of this.qualityNotifications) {
                    // dismiss quality notifications
                    this.dismissQualityNotificationAction(
                        notification['eventUUID']
                    );
                }
                for (const notification of this.eventAlertNotifications) {
                    // dismiss quality notifications
                    this.dismissEventAlertNotificationAction(
                        notification['eventUUID']
                    );
                }
            } catch (error) {
                console.error(
                    'Error dismissing quality notification due to: ',
                    error
                );
            }
        },
        /**
         * Close the notification popup dialog.
         */
        close() {
            this.showNotificationPopupAction(false);
            this.markAllNotificationsAsOldAction();
        },
        /**
         * Sort notifications by received at
         * @param {Array} notifications
         */
        sortNotificationsByReceivedAt(notifications) {
            notifications.sort((a, b) => {
                return new Date(b.receivedAt) - new Date(a.receivedAt);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.notification {
    width: 550px;
}

.justification-notification-header {
    height: 65px;
    background: $secondary-white;
}

.event-alert-notification-header {
    height: 65px;
    background-color: $background-red !important;
}

.notification-filters {
    border-bottom: solid 1px #e0e0e0;
}

.notification-body {
    height: 550px;
    overflow-y: auto;
}

.layout {
    height: 585px;
}

.list-complete-item {
    transition: all 0.6s;
}
.list-complete-enter,
.list-complete-leave-to {
    opacity: 0;
    transform: translateY(30px);
}
.list-complete-leave-active {
    position: absolute;
}

.font-improve {
    font-size: 1.3rem;
}

.no-scroll {
    overflow: hidden; /* Hide scrollbars */
}

.section-title {
    font-size: 17px;
    font-weight: 500;
}
.event-alert-text-color {
    color: red;
}
.justification-text-color {
    color: black;
}
</style>
